import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider as JotaiProvider } from "jotai";
import { useHydrateAtoms } from "jotai/react/utils";
import { queryClientAtom } from "jotai-tanstack-query";
import mixpanel from "mixpanel-browser";
import { ThemeProvider } from "./components/ThemeProvider";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import "./index.css";
import { isMixpanelTrackingEnabled } from "./lib/analytics";
import { logger } from "./lib/logger";
import { initGrafanaFaro } from "./services/grafanaFaro";

const LOG_NAMESPACE = "ENTRY";

// Initialize Mixpanel
if (isMixpanelTrackingEnabled()) {
  const MIXPANEL_TOKEN =
    import.meta.env.VITE_MIXPANEL_TOKEN ?? "ed683e80f9f22f6aa9e4b50b55610fed";
  mixpanel.init(MIXPANEL_TOKEN, {
    autocapture: true,
    record_sessions_percent: 100,
    api_host: "https://api-eu.mixpanel.com",
  });
  mixpanel.register({
    device_type: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
  });
} else {
  logger.debug(LOG_NAMESPACE, "Mixpanel tracking disabled.");
}

logger.debug(LOG_NAMESPACE, "Main.tsx rendering...");
initGrafanaFaro();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      // Refetch behavior: Conservative defaults to prevent excessive API calls
      refetchOnWindowFocus: false, // Disable refetching when user switches browser tabs (prevents redundant get-session calls)
      refetchOnMount: true, // Refetch when components mount (keeps data fresh on navigation)
      refetchOnReconnect: true, // Refetch when network reconnects (important for reliability)
    },
  },
});

// Hydrate atoms with QueryClient
function HydrateAtoms({ children }: { children: React.ReactNode }) {
  useHydrateAtoms(new Map([[queryClientAtom, queryClient]]));
  return <>{children}</>;
}

const AppComponent = lazy(() => import("./NewApp"));

logger.debug(LOG_NAMESPACE, "Main.tsx rendering NewApp...");

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary componentName="App" level="root">
      <QueryClientProvider client={queryClient}>
        <JotaiProvider>
          <HydrateAtoms>
            <ThemeProvider defaultTheme="light" storageKey="finmap-ui-theme">
              <Suspense
                fallback={<div className="min-h-screen bg-background" />}
              >
                <AppComponent />
              </Suspense>
            </ThemeProvider>
          </HydrateAtoms>
        </JotaiProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
